import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { ExportMenu } from '../../components/AppToolbar';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { runLoading } from '../../components/loading';
import { fmtLocalDate, fmtLocalDateTime, nowClientWire } from '../../utils/datetime';
import { AiCombo } from '../../components/AiCombo';
import { CheckedCombo } from '../../components/CheckedCombo';
import { useModalKeys } from '../../hooks/useModalKeys';
import { useAuthStore } from '../../auth/store';
import { StxModalHead } from './StxModalHead';
// The Samples/Tests/Containers popups are no longer imported here — the pills now
// raise onOpenPill and the detail page swaps itself for the full page.
import type { PillTab } from './SampleTransferSamplesPage';

// ─── Types (match the API DTOs) ──────────────────────────────────────────────
export interface ReceivedRow {
  receivedDateOid: string; receivedDate: string | null; receivedBy: string;
  relinquishedBy: string; noOfSamples: number; coolerId: string;
}
interface ReceivedDetail {
  oid: string; receivedDate: string | null; receivedBy: string; relinquishedBy: string;
  relinquishedById: string | null; noOfSamples: number; sampleMatrix: string; tat: string;
  coolerId: string; sampleCategory: string; createdBy: string; createdDate: string | null;
  receivedById: string | null; tatId: string | null; sampleMatrixId: string; sampleCategoryId: string;
}
interface Lookups {
  employees: { id: string; name: string }[]; turnarounds: { id: string; name: string }[];
  matrices: { id: string; name: string }[]; categories: { id: string; name: string }[];
}
/** The read-only Sample Condition Check popup payload. */
interface SccDetail {
  oid: string; jobId: string; client: string; checkInBy: string; checkInDate: string | null;
  sampleMatrix: string; temperature: string; correctedTemperature: string;
  sampleMatrixIds: string; matrixOptions: SccOption[]; receivedDateOptions: string[];
  comments: SccComment[];
  thermometerId: string; samplePh: string; phPaperId: string; receivedDate: string;
  checkPoints: SccPoint[];
}
interface SccPoint {
  oid: string;
  /** The dbo.SampleConditionCheckData master - the stable key, because a seeded
   *  row has no Oid of its own until the first save. */
  checkPointId: string;
  checkPoint: string; yes: boolean; no: boolean; na: boolean; description: string;
}
/** One row of the popup's Comment grid. A row added in the popup gets a
 *  "new-…" client id until the save mints a real Oid. */
interface SccComment {
  oid: string; note: string; user: string; dateTime: string | null;
}
interface SccOption { id: string; name: string }

interface Attachment {
  oid: string; name: string; category: number; categoryName: string;
  fileOid: string | null; fileName: string; receivedDate: string; date: string;
  operator: string; comment: string;
}
interface NoteRow {
  oid: string; title: string; note: string; author: string; date: string | null; canEdit: boolean;
  sort: number; html: string; attachmentOid: string | null; attachmentName: string;
}
interface SccRow {
  oid: string; sampleMatrix: string; checkInBy: string; checkInDate: string | null;
  receivedDate: string; correctedTemperature: string;
}

const CATEGORIES = ['COC', 'Image', 'Pdf Report', 'Result EDD', 'Subout', 'Other'];

/** A naive/ISO wire datetime → an <input type="datetime-local"> value, MINUTE
 *  precision (YYYY-MM-DDTHH:mm — no seconds). Legacy shows the Received Date to the
 *  minute, so the input hides the seconds field too (no step="1"; user request
 *  2026-10-20). Byte-identity with the stored value (which may carry seconds) is
 *  still preserved by receivedDateWire: when the user does NOT change the date, the
 *  ORIGINAL ISO is returned verbatim, so the EXACT ReceivedDate = @ReceivedDate
 *  match (SampleTransferReader.ReceivedDatesAsync) still holds and the row does not
 *  drop out of the scoped tab after a save that only touched other fields. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : '';
}

/**
 * Wire value for the Received-Date edit's ReceivedDate.
 *
 * The Received Dates tab is filtered by an EXACT `ReceivedDate = @ReceivedDate`
 * match (SampleTransferReader.ReceivedDatesAsync), where `@ReceivedDate` is the
 * transfer's own ReceivedDate — so ANY drift in this row's ReceivedDate drops it
 * out of the scoped tab on reload. When the user did not touch the date field we
 * therefore return the ORIGINAL ISO verbatim (byte-identical, whatever its
 * precision), so editing any other field leaves the row in place. Only a
 * deliberate change sends a new value, at minute precision (seconds = 00).
 */
export function receivedDateWire(originalIso: string | null, inputValue: string): string | null {
  if (inputValue === toLocalInput(originalIso)) return originalIso;
  if (!inputValue) return null;
  return /T\d{2}:\d{2}:\d{2}/.test(inputValue) ? inputValue : `${inputValue}:00`;
}

type TabKey = 'received' | 'attachments' | 'scc' | 'notes';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'received', label: 'Received Dates' },
  { key: 'attachments', label: 'Attachments' },
  { key: 'scc', label: 'Sample Condition Check' },
  { key: 'notes', label: 'Notes' },
];

interface Props {
  jobOid: string;
  jobId: string;
  /** THIS transfer's Received Date. Legacy scopes the Received Dates tab to the
   *  one row that matches it (SampleTransferViewController.cs:
   *  [SampleCheckin.Oid] = ? and [ReceivedDate] = ?), not to the whole job. */
  receivedDate?: string | null;
  receivedDates: ReceivedRow[];
  samplesCount: number;
  testsCount: number;
  containersCount: number;
  canWrite?: boolean;
  /** MAC action flags (Role-Based MAC). canCreate gates New, canWrite gates Edit /
   *  inline edits, canDelete gates Delete — default to canWrite for back-compat. */
  canCreate?: boolean;
  canDelete?: boolean;
  broughtBy?: { id: string; name: string }[];
  /** The job header's Relinquished By set (lowercased BroughtBy Oids). The
   *  Received-Date edit popup scopes its Relinquished By combo to THESE only,
   *  mirroring legacy SC_ReceivedDates.RelinquishedByDataSource (BroughtBy Oid IN
   *  SampleCheckin.RelinquishedBy) — not the full BroughtBy master. */
  relinquishers?: Set<string>;
  /** Open the Samples / Tests / Containers FULL PAGE (user request 2026-09-23 —
   *  these were three modals; the detail page swaps itself for the page). */
  onOpenPill: (pill: PillTab) => void;
}

const API_BASE = '/api/v1/sample-transfer';

/** Bottom tabbed area of the Sample Transfer detail (legacy DetailView parity):
 *  the Samples/Tests/Containers pills + Received Dates · Attachments · Sample
 *  Condition Check · Notes. Each tab reads the job-scoped legacy table on demand.
 *  Attachments + Notes + Sample Condition Check are full CRUD; the SCC tab also
 *  previews the SCC_CheckPoints_Report PDF for the selected row(s). */
export default function SampleTransferBottomTabs({
  jobOid, jobId, receivedDate, samplesCount, testsCount, containersCount,
  canWrite = false, canCreate = canWrite, canDelete = canWrite, broughtBy = [],
  relinquishers,
  onOpenPill,
}: Props) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [tab, setTab] = useState<TabKey>('received');
  // Maximize the active tab's grid — the control lives on the tab TOOLBAR row (not a
  // separate grid header strip; user request 2026-09-22). Esc restores. One state is
  // enough since only the active tab's grid is mounted at a time.
  const [tabMax, setTabMax] = useState(false);
  useEffect(() => {
    if (!tabMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setTabMax(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tabMax]);
  // Restore from maximize whenever the tab changes (the maximized grid unmounts).
  useEffect(() => { setTabMax(false); }, [tab]);
  const gridCls = tabMax ? 'adg-maximized stx-list-maxed' : undefined;
  const maxBtn = (
    <button type="button" className="adg-max-btn stx-tab-max" title="Maximize" aria-label="Maximize"
      onClick={() => setTabMax(true)}>
      <i className="ti ti-arrows-maximize" aria-hidden="true" />
    </button>
  );

  const [received, setReceived] = useState<ReceivedDetail[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [notes, setNotes] = useState<NoteRow[]>([]);
  const [scc, setScc] = useState<SccRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [recSel, setRecSel] = useState<Set<string | number>>(new Set());
  const [attSel, setAttSel] = useState<Set<string | number>>(new Set());
  const [noteSel, setNoteSel] = useState<Set<string | number>>(new Set());

  const [recDialog, setRecDialog] = useState<ReceivedDetail | null>(null);
  const [attDialog, setAttDialog] = useState<Attachment | 'new' | null>(null);
  /** The Sample Condition Check whose popup is open, or 'new' for a fresh one
   *  (ALPACA's SampleConditionCheck_DetailView_Copy). */
  const [sccDialog, setSccDialog] = useState<SccRow | 'new' | null>(null);
  const [sccSel, setSccSel] = useState<Set<string | number>>(new Set());
  const [noteDialog, setNoteDialog] = useState<NoteRow | 'new' | null>(null);

  /** The transfer Received Date as legacy stores it on dbo.Attachment and
   *  dbo.SampleConditionCheck: an nvarchar in "MM/dd/yyyy HH:mm". Both tabs are
   *  filtered on that STRING in ALPACA
   *  (SampleTransferViewController.cs:1858-1872), which is why they are empty
   *  there for a transfer that has no attachment / condition check of its own,
   *  while ours listed the whole job (owner report 2026-09-28). fmtLocalDateTime
   *  is the shared helper and already emits exactly MM/DD/YYYY HH:mm. */
  const receivedKey = receivedDate ? fmtLocalDateTime(receivedDate) : undefined;

  /** Options for the Attachment popup's Received Date COMBO — ALPACA renders it
   *  as an ASPxComboBoxEditor filled from the session's received-date list and
   *  DEFAULTS a new attachment to its first entry
   *  (ASPxComboboxPropertyEditor_new.cs:146-158). On this form that list is the
   *  transfer's own date, which is also what the tab filters on — so a new
   *  attachment lands in the list it was created from instead of saving with a
   *  blank ReceivedDate and vanishing. */
  const receivedOptions = useMemo(() => {
    const list = received.map(r => (r.receivedDate ? fmtLocalDateTime(r.receivedDate) : '')).filter(Boolean);
    if (receivedKey) list.unshift(receivedKey);
    return Array.from(new Set(list));
  }, [received, receivedKey]);

  const loadReceived = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API_BASE}/${jobOid}/received-dates`,
        receivedDate ? { params: { receivedDate } } : undefined);
      setReceived((res.data as { data: ReceivedDetail[] }).data ?? []);
      setRecSel(new Set());
    } finally { setLoading(false); }
  }, [jobOid, receivedDate]);

  const loadAttachments = useCallback(async () => {
    setLoading(true);
    try {
      // Legacy parity (SampleTransferViewController.cs:1862): the Attachments tab is
      // SCOPED to the transfer's Received Date — CriteriaOperator [Samplecheckin.Oid]=job
      // AND [ReceivedDate]=<received date "MM/dd/yyyy HH:mm">. So an attachment with a
      // blank / other ReceivedDate (e.g. a job-level upload) does NOT show here, matching
      // legacy (owner decision 2026-10-22, JobID 261013001: legacy 1 row vs AI's 2 — the
      // extra one, IH_report, had a NULL ReceivedDate). New attachments default to the
      // transfer's date (receivedOptions) so they match. When the detail has no received
      // date, receivedKey is undefined → the reader lists job-wide. This re-scopes the
      // list that was job-wide since 2026-10-07; as in legacy, a transfer with no
      // attachment of its OWN received date now shows "No data" (owner-accepted trade-off).
      const res = await api.get(`${API_BASE}/${jobOid}/attachments`,
        receivedKey ? { params: { receivedDate: receivedKey } } : undefined);
      setAttachments((res.data as { data: Attachment[] }).data ?? []);
      setAttSel(new Set());
    } finally { setLoading(false); }
  }, [jobOid, receivedKey]);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API_BASE}/${jobOid}/notes-tab`);
      setNotes((res.data as { data: NoteRow[] }).data ?? []);
      setNoteSel(new Set());
    } finally { setLoading(false); }
  }, [jobOid, receivedKey]);

  const loadScc = useCallback(async () => {
    setLoading(true);
    try {
      // Show ALL condition checks for the job — same job-level reasoning as attachments
      // above (not filtered by the transfer's Received Date), so they don't vanish after
      // a Save gives the transfer a Received Date. (bug fix 2026-10-07)
      const res = await api.get(`${API_BASE}/${jobOid}/condition-checks`);
      setScc((res.data as { data: SccRow[] }).data ?? []);
    } finally { setLoading(false); }
  }, [jobOid]);

  // Received Dates is the default tab — load its full column set on mount.
  useEffect(() => { void loadReceived(); }, [loadReceived]);

  // Load the other tabs the first time each is opened.
  const loaded = useRef<Set<TabKey>>(new Set(['received']));
  useEffect(() => {
    if (loaded.current.has(tab)) return;
    loaded.current.add(tab);
    if (tab === 'attachments') void loadAttachments();
    else if (tab === 'notes') void loadNotes();
    else if (tab === 'scc') void loadScc();
  }, [tab, loadAttachments, loadNotes, loadScc]);

  const download = useCallback(async (fileOid: string, fileName: string) => {
    try {
      const res = await runLoading(
        api.get(`${API_BASE}/attachments/file/${fileOid}/download`, { responseType: 'blob' }),
        { blocking: true, label: 'Downloading…' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName || 'attachment'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { notify.error('Failed to download the file.'); }
  }, [notify]);

  // ── Received Dates columns (full legacy set) ────────────────────────────────
  const recCols: AppColumn<ReceivedDetail>[] = [
    // Row Edit pencil (legacy) — opens the Edit popup for that row directly.
    ...(canWrite ? [{
      // Narrow icon-only column. The default 12px cell padding each side can't fit a
      // 30px column, so `stx-editcell` trims it to ~2px (owner report 2026-10-22:
      // "reduce edit column width"). Centred.
      field: '_edit', header: '', width: 30, align: 'center' as const,
      cellClass: () => 'stx-editcell', sortable: false, filterable: false, noExport: true,
      render: (r: ReceivedDetail) => (
        <button type="button" className="stx-editpen" title="Edit"
          onClick={e => { e.stopPropagation(); setRecDialog(r); }}><i className="ti ti-edit" /></button>
      ),
    } as AppColumn<ReceivedDetail>] : []),
    { field: 'receivedDate', header: 'ReceivedDate', width: 150,
      text: r => r.receivedDate ?? '', render: r => r.receivedDate ? fmtLocalDateTime(r.receivedDate) : '—' },
    { field: 'receivedBy', header: 'ReceivedBy', width: 130 },
    { field: 'relinquishedBy', header: 'RelinquishedBy', width: 130 },
    { field: 'noOfSamples', header: 'NoOfSamples', width: 110, align: 'right' },
    { field: 'sampleMatrix', header: 'SampleMatrix', width: 130 },
    { field: 'tat', header: 'TAT', width: 90 },
    { field: 'coolerId', header: 'CoolerID', width: 90 },
    { field: 'sampleCategory', header: 'SampleCategory', width: 130 },
    { field: 'createdBy', header: 'CreatedBy', width: 130 },
    { field: 'createdDate', header: 'CreatedDate', width: 110,
      text: r => r.createdDate ?? '', render: r => r.createdDate ? fmtLocalDate(r.createdDate) : '—' },
  ];

  // ── Attachments columns + actions ───────────────────────────────────────────
  const attCols: AppColumn<Attachment>[] = [
    // Row edit pencil - ALPACA puts one at the start of every attachment row
    // (owner request 2026-09-28); it opens the same popup as the toolbar Edit.
    { field: '__edit', header: '', width: 34, align: 'center', sortable: false,
      filterable: false, noExport: true,
      render: r => canWrite ? (
        <button type="button" className="stx-editpen" title="Edit"
                onClick={e => { e.stopPropagation(); setAttDialog(r); }}>
          <i className="ti ti-edit" />
        </button>
      ) : null },
    { field: 'name', header: 'Name', width: 200 },
    { field: 'categoryName', header: 'Category', width: 120 },
    {
      field: 'fileName', header: 'Attachment', width: 200,
      text: r => r.fileName,
      render: r => r.fileOid
        ? <button type="button" className="stx-link stx-linkbtn" onClick={e => { e.stopPropagation(); void download(r.fileOid!, r.fileName); }}>{r.fileName || 'Download'}</button>
        : <span className="stx-muted">—</span>,
    },
    { field: 'date', header: 'Date', width: 110, text: r => r.date, render: r => r.date ? fmtLocalDate(r.date) : '—' },
    { field: 'operator', header: 'Operator', width: 150 },
    { field: 'receivedDate', header: 'ReceivedDate', width: 150, render: r => r.receivedDate || '—' },
  ];

  const sccCols: AppColumn<SccRow>[] = [
    // Row edit pencil, as ALPACA shows on every Sample Condition Check row.
    { field: '__edit', header: '', width: 34, align: 'center', sortable: false,
      filterable: false, noExport: true,
      render: r => canWrite ? (
        <button type="button" className="stx-editpen" title="Open"
                onClick={e => { e.stopPropagation(); setSccDialog(r); }}>
          <i className="ti ti-edit" />
        </button>
      ) : null },
    { field: 'sampleMatrix', header: 'SampleMatrix', width: 200 },
    { field: 'checkInBy', header: 'CheckInBy', width: 170 },
    { field: 'checkInDate', header: 'CheckInDate', width: 150,
      text: r => r.checkInDate ?? '', render: r => r.checkInDate ? fmtLocalDateTime(r.checkInDate) : '—' },
    { field: 'receivedDate', header: 'ReceivedDate', width: 150, render: r => r.receivedDate || '—' },
    { field: 'correctedTemperature', header: 'CorrectedTemperature', width: 170, render: r => r.correctedTemperature || '—' },
  ];

  const noteCols: AppColumn<NoteRow>[] = [
    { field: 'title', header: 'Title', width: 180 },
    { field: 'note', header: 'Note', width: 300 },
    { field: 'attachmentName', header: 'Attachment', width: 170,
      text: r => r.attachmentName,
      render: r => r.attachmentOid
        ? <button type="button" className="stx-link stx-linkbtn" onClick={e => { e.stopPropagation(); void download(r.attachmentOid!, r.attachmentName); }}>{r.attachmentName || 'Download'}</button>
        : '' },
    { field: 'author', header: 'Author', width: 150 },
    { field: 'date', header: 'Date', width: 150,
      text: r => r.date ?? '', render: r => r.date ? fmtLocalDateTime(r.date) : '—' },
    { field: 'sort', header: 'sort', type: 'number', width: 70 },
  ];

  // NOTE: no deleteAttachments here - the Delete action is hidden on this tab to
  // match legacy (see the toolbar). The API route still exists for other callers.

  const deleteNotes = useCallback(async () => {
    const ids = [...noteSel].map(String);
    if (ids.length === 0) return;
    const ok = await confirm({ title: 'Delete note', message: `Delete ${ids.length} note(s)?`, tone: 'danger' });
    if (!ok) return;
    try {
      await runLoading(Promise.all(ids.map(id => api.delete(`${API_BASE}/${jobOid}/notes-tab/${id}`))),
        { blocking: true, label: 'Deleting…' });
      notify.success('Deleted successfully.');
      await loadNotes();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      notify.error(msg || 'Failed to delete.');
    }
  }, [noteSel, confirm, jobOid, loadNotes, notify]);

  const deleteSccs = useCallback(async () => {
    const ids = [...sccSel].map(String);
    if (ids.length === 0) return;
    const ok = await confirm({
      title: 'Delete sample condition check',
      message: `Delete ${ids.length} sample condition check(s)?`, tone: 'danger' });
    if (!ok) return;
    try {
      await runLoading(Promise.all(ids.map(id => api.delete(`${API_BASE}/${jobOid}/condition-checks/${id}`))),
        { blocking: true, label: 'Deleting…' });
      notify.success('Deleted successfully.');
      setSccSel(new Set());
      await loadScc();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      notify.error(msg || 'Failed to delete.');
    }
  }, [sccSel, confirm, jobOid, loadScc, notify]);

  // SCC Report → the SCC_CheckPoints_Report PDF for the selected row(s) (legacy scopes
  // to View.SelectedObjects); with nothing ticked, report every row shown on the tab
  // (already scoped to this transfer). Opens inline like the Reports ▾ preview.
  const sccReport = useCallback(async () => {
    const ids = (sccSel.size > 0 ? [...sccSel] : scc.map(r => r.oid)).map(String);
    if (ids.length === 0) { notify.warning('There is no condition check to report.'); return; }
    try {
      const res = await runLoading(
        api.get(`${API_BASE}/scc-report`, { params: { sccOids: ids.join(',') }, responseType: 'blob' }),
        { blocking: true, label: 'Preparing SCC report…' });
      const blob = res.data as Blob;
      if (res.status === 204 || !blob || blob.size === 0) { notify.warning('No data to render for the SCC report.'); return; }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { notify.error('Failed to generate the SCC report.'); }
  }, [sccSel, scc, notify]);

  const recSelOne = recSel.size === 1 ? received.find(r => r.oid === [...recSel][0]) : undefined;
  const attSelOne = attSel.size === 1 ? attachments.find(a => a.oid === [...attSel][0]) : undefined;
  const noteSelOne = noteSel.size === 1 ? notes.find(n => n.oid === [...noteSel][0]) : undefined;
  const sccSelOne  = sccSel.size === 1 ? scc.find(r => r.oid === [...sccSel][0]) : undefined;

  return (
    <div className="stx-tabs-wrap">
      {/* Tab bar */}
      <div className="stx-tabbar" role="tablist">
        {TABS.map(t => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
            className={`stx-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="stx-tabpanel">
        {tab === 'received' && (
          <>
            <div className="stx-tab-toolbar">
              {canWrite && <button className="stx-tbtn" disabled={!recSelOne} onClick={() => recSelOne && setRecDialog(recSelOne)}><i className="ti ti-edit" /> Edit</button>}
              <ExportMenu triggerClassName="stx-tbtn" filename={`${jobId}_ReceivedDates`} {...gridExportMatrix(received, recCols)} />
              {maxBtn}
            </div>
            <AppDataGrid<ReceivedDetail> rows={received} columns={recCols} getRowId={r => r.oid}
              selectable selectedIds={recSel} onSelectionChange={setRecSel}
              autoFocusFirstRow={false}
              hideHeaderTools
              /* Honor each column's configured width exactly so the empty-header Edit
                 column stays narrow (38px) instead of the AUTO table-layout stretching it
                 to fill the container (owner report 2026-10-22: the pencil column was too
                 wide, pushing ReceivedDate right). Matches legacy's compact grid. */
              enforceWidths
              className={gridCls}
              loading={loading} emptyText="No data to display" />
          </>
        )}

        {tab === 'attachments' && (
          <>
            <div className="stx-tab-toolbar">
              {canCreate && <button className="stx-tbtn" onClick={() => setAttDialog('new')}><i className="ti ti-plus" /> New</button>}
              {canWrite && <button className="stx-tbtn" disabled={!attSelOne} onClick={() => attSelOne && setAttDialog(attSelOne)}><i className="ti ti-edit" /> Edit</button>}
              {/* NO Delete on this tab - legacy parity (owner report 2026-09-28).
                  The Sample Transfer DetailView hides the action outright:
                  SampleBottleAllocation_DetailView_SampleTransfer carries
                  AllowDelete="False" plus <ActionLink ActionId="Delete"/> in its
                  HiddenActions (Model.xafml), which suppresses it across the view
                  including this nested Attachments list - so ALPACA shows only
                  New / Edit / Export to. Attachments are deleted from the form that
                  owns them, not from the transfer. */}
              <ExportMenu triggerClassName="stx-tbtn" filename={`${jobId}_Attachments`} {...gridExportMatrix(attachments, attCols)} />
              {maxBtn}
            </div>
            <AppDataGrid<Attachment> rows={attachments} columns={attCols} getRowId={r => r.oid}
              selectable selectedIds={attSel} onSelectionChange={setAttSel}
              hideHeaderTools
              className={gridCls}
              loading={loading} emptyText="No data to display" />
          </>
        )}

        {tab === 'scc' && (
          <>
            {/* Legacy toolbar: New · Edit · Delete · SCC Report · Export to. */}
            <div className="stx-tab-toolbar">
              {canCreate && <button className="stx-tbtn" onClick={() => setSccDialog('new')}><i className="ti ti-plus" /> New</button>}
              {canWrite && <button className="stx-tbtn" disabled={!sccSelOne} onClick={() => sccSelOne && setSccDialog(sccSelOne)}><i className="ti ti-edit" /> Edit</button>}
              {canDelete && <button className="stx-tbtn" disabled={sccSel.size === 0} onClick={() => void deleteSccs()}><i className="ti ti-trash" /> Delete</button>}
              <button className="stx-tbtn" disabled={scc.length === 0} onClick={() => void sccReport()}><i className="ti ti-file-text" /> SCC Report</button>
              <ExportMenu triggerClassName="stx-tbtn" filename={`${jobId}_SampleConditionCheck`} {...gridExportMatrix(scc, sccCols)} />
              {maxBtn}
            </div>
            <AppDataGrid<SccRow> rows={scc} columns={sccCols} getRowId={r => r.oid}
              selectable selectedIds={sccSel} onSelectionChange={setSccSel}
              hideHeaderTools
              className={gridCls}
              loading={loading} emptyText="No data to display" />
          </>
        )}

        {tab === 'notes' && (
          <>
            <div className="stx-tab-toolbar">
              {canCreate && <button className="stx-tbtn" onClick={() => setNoteDialog('new')}><i className="ti ti-plus" /> New</button>}
              {canWrite && <button className="stx-tbtn" disabled={!noteSelOne || !noteSelOne.canEdit} onClick={() => noteSelOne && setNoteDialog(noteSelOne)}><i className="ti ti-edit" /> Edit</button>}
              {canDelete && <button className="stx-tbtn" disabled={noteSel.size === 0 || [...noteSel].some(id => !notes.find(n => n.oid === id)?.canEdit)} onClick={deleteNotes}><i className="ti ti-trash" /> Delete</button>}
              <ExportMenu triggerClassName="stx-tbtn" filename={`${jobId}_Notes`} {...gridExportMatrix(notes, noteCols)} />
              {maxBtn}
            </div>
            <AppDataGrid<NoteRow> rows={notes} columns={noteCols} getRowId={r => r.oid}
              selectable selectedIds={noteSel} onSelectionChange={setNoteSel}
              hideHeaderTools
              className={gridCls}
              loading={loading} emptyText="No data to display" />
          </>
        )}
      </div>

      {/* Restore bar for the maximized tab grid — its own row so it never overlaps
          the column headers (mirrors the list). */}
      {tabMax && (
        <div className="stx-list-maxbar">
          <button type="button" className="adg-max-btn" title="Restore (Esc)" aria-label="Restore"
            onClick={() => setTabMax(false)}>
            <i className="ti ti-arrows-minimize" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Summary pills — CLICKABLE, open the legacy Sample Login / Tests / Containers
          popups (user request 2026-09-22). At the BOTTOM (legacy DetailView). */}
      <div className="stx-pills">
        <button type="button" className="stx-pill" onClick={() => onOpenPill('samples')}>Samples({samplesCount})</button>
        <button type="button" className="stx-pill" onClick={() => onOpenPill('tests')}>Tests({testsCount})</button>
        <button type="button" className="stx-pill" onClick={() => onOpenPill('containers')}>Containers({containersCount})</button>
      </div>

      {recDialog && (
        <ReceivedDateDialog jobOid={jobOid} value={recDialog} broughtBy={broughtBy} relinquishers={relinquishers}
          onClose={() => setRecDialog(null)}
          onSaved={async () => { setRecDialog(null); await loadReceived(); }} />
      )}
      {sccDialog && (
        <ConditionCheckDialog
          jobOid={jobOid}
          sccOid={sccDialog === 'new' ? null : sccDialog.oid}
          receivedDate={receivedKey}
          onClose={() => setSccDialog(null)}
          onSaved={async () => { setSccDialog(null); setSccSel(new Set()); await loadScc(); }} />
      )}
      {attDialog && (
        <AttachmentDialog jobOid={jobOid} value={attDialog === 'new' ? null : attDialog}
          receivedOptions={receivedOptions}
          onClose={() => setAttDialog(null)}
          onSaved={async () => { setAttDialog(null); await loadAttachments(); }} />
      )}
      {noteDialog && (
        <NoteDialog jobOid={jobOid} value={noteDialog === 'new' ? null : noteDialog}
          onClose={() => setNoteDialog(null)}
          onSaved={async () => { setNoteDialog(null); await loadNotes(); }} />
      )}
    </div>
  );
}

// ─── Attachment New/Edit dialog ──────────────────────────────────────────────
/** The Sample Condition Check popup - ALPACA's SampleConditionCheck_DetailView_Copy:
 *  a two-column header (Job ID / Client / Check In By / Check In Date / Sample Matrix on
 *  the left, Temperature / Corrected Temperature / Thermometer ID / Sample pH / pH Paper
 *  ID / Received Date on the right), then the Check Points grid, then the Comment grid,
 *  then OK / Cancel.
 *
 *  EDITABLE since 2026-09-28. Job ID / Client / Check In By / Check In Date carry
 *  AllowEdit="False" in the legacy model, so they stay read-only; everything else is
 *  editable. The three answer boxes are MUTUALLY EXCLUSIVE - the legacy BO keeps three
 *  independent bits, but the master's PickAnswer only ever sets one and the grid reads as
 *  a tri-state, so ticking one clears the others (and re-ticking clears the row, which is
 *  how an unanswered legacy row looks).
 *
 *  The whole popup is ONE unit of work, matching XAF: the header, every check point and
 *  every comment are buffered here and committed by a single POST when OK is pressed -
 *  the same commit boundary ALPACA's DialogController.AcceptAction gives it. */
function ConditionCheckDialog({ jobOid, sccOid, receivedDate, onClose, onSaved }: {
  jobOid: string; sccOid: string | null; receivedDate?: string;
  onClose: () => void; onSaved: () => void | Promise<void>;
}) {
  const notify = useNotify();
  const { confirm, prompt } = useConfirm();
  const currentUser = useAuthStore(s => s.user?.name ?? '');
  const [detail, setDetail] = useState<SccDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [points, setPoints] = useState<SccPoint[]>([]);
  const [comments, setComments] = useState<SccComment[]>([]);
  const [matrixIds, setMatrixIds] = useState('');
  const [temperature, setTemperature] = useState('');
  const [correctedTemperature, setCorrectedTemperature] = useState('');
  const [thermometerId, setThermometerId] = useState('');
  const [samplePh, setSamplePh] = useState('');
  const [phPaperId, setPhPaperId] = useState('');
  const [recvDate, setRecvDate] = useState('');
  const [cmtSel, setCmtSel] = useState<Set<string | number>>(new Set());
  // Check Points row selection — legacy grid has a leading checkbox column
  // (select-all + per-row). Kept for parity; the popup OK saves regardless.
  const [cpSel, setCpSel] = useState<Set<string | number>>(new Set());
  // Check Points grid batch-edit state (legacy DevExpress "Save changes / Cancel
  // changes" at the grid bottom): edits go to `points`; `committedPoints` is the
  // baseline; `cpDirty` toggles the two grid buttons. The popup OK saves `points`.
  const [cpDirty, setCpDirty] = useState(false);
  const committedPoints = useRef<SccPoint[]>([]);
  const modalKeys = useModalKeys(true, { onClose });

  // SCC Report inside the popup (legacy PopupSCC_Report): ENABLED only for an
  // existing record (Edit), DISABLED for a new one — a new SCC has nothing to
  // report yet (SampleConditionCheckViewController L54-60).
  const sccReportInPopup = useCallback(async () => {
    if (!sccOid) return;
    try {
      const res = await runLoading(
        api.get(`${API_BASE}/scc-report`, { params: { sccOids: sccOid }, responseType: 'blob' }),
        { blocking: true, label: 'Preparing SCC report…' });
      const blob = res.data as Blob;
      if (res.status === 204 || !blob || blob.size === 0) { notify.warning('No data to render for the SCC report.'); return; }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { notify.error('Failed to generate the SCC report.'); }
  }, [sccOid, notify]);

  const commitPoints = useCallback(() => { committedPoints.current = points; setCpDirty(false); }, [points]);
  const revertPoints = useCallback(() => { setPoints(committedPoints.current); setCpDirty(false); }, []);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const url = sccOid
          ? `${API_BASE}/condition-checks/${sccOid}`
          : `${API_BASE}/${jobOid}/condition-checks/new`;
        const res = await api.get(url, sccOid || !receivedDate ? undefined : { params: { receivedDate } });
        const d = (res.data as { data: SccDetail }).data;
        setDetail(d);
        setPoints(d.checkPoints ?? []);
        committedPoints.current = d.checkPoints ?? [];
        setCpDirty(false);
        setComments(d.comments ?? []);
        setMatrixIds(d.sampleMatrixIds ?? '');
        setTemperature(d.temperature ?? '');
        setCorrectedTemperature(d.correctedTemperature ?? '');
        setThermometerId(d.thermometerId ?? '');
        setSamplePh(d.samplePh ?? '');
        setPhPaperId(d.phPaperId ?? '');
        // Received Date defaults to the transfer's own date on NEW, matching legacy
        // (the New SCC opens with Received Date pre-filled — owner report 2026-10-22).
        // The server echoes the passed receivedDate; if it came through empty, fall back
        // to the first option (SC_ReceivedDates newest-first = the transfer's date). An
        // EXISTING SCC keeps its stored value, no fallback.
        setRecvDate(d.receivedDate || (sccOid ? '' : (d.receivedDateOptions?.[0] ?? '')));
      } catch { notify.error('Failed to load the sample condition check.'); }
      finally { setLoading(false); }
    })();
  }, [sccOid, jobOid, receivedDate, notify]);

  /** The legacy answer columns are three bits but behave as one tri-state. Keyed on the
   *  MASTER id, because a seeded row has no Oid of its own until the first save. */
  const answer = useCallback((key: string, which: 'yes' | 'no' | 'na') => {
    setCpDirty(true);
    setPoints(prev => prev.map(p => {
      if (p.checkPointId !== key) return p;
      const on = !p[which];
      return { ...p, yes: which === 'yes' && on, no: which === 'no' && on, na: which === 'na' && on };
    }));
  }, []);

  const describe = useCallback((key: string, text: string) => {
    setCpDirty(true);
    setPoints(prev => prev.map(p => (p.checkPointId === key ? { ...p, description: text } : p)));
  }, []);

  // The matrix editor works in NAMES and is mapped back to Oids at this boundary - the
  // shared CheckedCombo is a string list, and VisualMatrixName is unique.
  const matrixOptions = detail?.matrixOptions ?? [];
  const nameById = new Map(matrixOptions.map(o => [o.id.toLowerCase(), o.name]));
  const idByName = new Map(matrixOptions.map(o => [o.name, o.id]));
  const matrixNames = matrixIds.split(';')
    .map(v => nameById.get(v.trim().toLowerCase()))
    .filter((n): n is string => !!n);

  const addComment = useCallback(async () => {
    // Legacy popup title (SampleConditionCheckComment_DetailView): "Sample
    // Condition Check Comment", not a generic "New comment" (user report 2026-10-20).
    const text = await prompt({ title: 'Sample Condition Check Comment', message: 'Comment', required: true });
    if (!text || !text.trim()) return;
    // Stamp User + DateTime immediately, mirroring legacy SampleConditionCheckComment
    // .AfterConstruction (User = current user, DateTime = server time) so the grid shows
    // them the moment the comment is added, not only after Save. The server re-stamps both
    // authoritatively on insert (@Actor / GETDATE()), so these are the optimistic display.
    setComments(prev => [...prev, {
      oid: `new-${Date.now()}-${prev.length}`, note: text.trim(),
      user: currentUser, dateTime: new Date().toISOString(),
    }]);
  }, [prompt, currentUser]);

  const editComment = useCallback(async (row: SccComment) => {
    const text = await prompt({ title: 'Edit comment', message: 'Comment', initialValue: row.note, required: true });
    if (text === null) return;
    setComments(prev => prev.map(c => (c.oid === row.oid ? { ...c, note: text.trim() } : c)));
  }, [prompt]);

  const removeComments = useCallback(async () => {
    if (cmtSel.size === 0) return;
    const ok = await confirm({ title: 'Delete comment', message: `Delete ${cmtSel.size} comment(s)?`, tone: 'danger' });
    if (!ok) return;
    setComments(prev => prev.filter(c => !cmtSel.has(c.oid)));
    setCmtSel(new Set());
  }, [cmtSel, confirm]);

  const save = useCallback(async () => {
    const ok = await confirm({ title: 'Save', message: 'Do you want to Save?' });
    if (!ok) return;
    try {
      await runLoading(api.post(`${API_BASE}/${jobOid}/condition-checks`, {
        // A seeded point and a brand-new comment have no Oid yet - the server mints one
        // and writes the link row, because both children are many-to-many.
        oid: sccOid,
        sampleMatrixIds: matrixIds,
        temperature, correctedTemperature, thermometerId, samplePh, phPaperId,
        receivedDate: recvDate,
        checkPoints: points.map(p => ({
          oid: p.oid && !p.oid.startsWith('00000000') ? p.oid : null,
          checkPointId: p.checkPointId,
          yes: p.yes, no: p.no, na: p.na, description: p.description ?? '',
        })),
        comments: comments.map(c => ({
          oid: c.oid.startsWith('new-') ? null : c.oid,
          note: c.note,
        })),
      }), { blocking: true, label: 'Saving…' });
      notify.success('Saved successfully.');
      await onSaved();
    } catch { notify.error('Failed to save the sample condition check.'); }
  }, [confirm, jobOid, sccOid, matrixIds, temperature, correctedTemperature, thermometerId,
      samplePh, phPaperId, recvDate, points, comments, notify, onSaved]);

  const ro = (label: string, value: string) => (
    <div className="stx-mrow"><label>{label}</label>
      <div className="stx-ro-mini">{value || '—'}</div></div>
  );
  const ed = (label: string, value: string, set: (v: string) => void) => (
    <div className="stx-mrow"><label>{label}</label>
      <input className="stx-edit" value={value} onChange={e => set(e.target.value)} /></div>
  );

  return (
    <div className="stx-modal-backdrop" onClick={onClose}>
      <div className="stx-modal stx-modal-wide stx-modal-scc" onClick={e => e.stopPropagation()} {...modalKeys}>
        <StxModalHead
          title={detail?.client ? `${detail.client} - Sample Condition Check` : 'Sample Condition Check'}
          onClose={onClose} />
        <div className="stx-modal-body">
          {loading && <div className="stx-muted">Loading…</div>}
          {!loading && detail && (
            <>
              {/* SCC Report (legacy PopupSCC_Report) — top-left, ENABLED only for an
                  existing record; a NEW SCC has nothing to report yet. */}
              <div className="stx-scc-topbar">
                <button className="stx-tbtn stx-primary" disabled={!sccOid}
                        onClick={() => void sccReportInPopup()}>
                  <i className="ti ti-file-text" /> SCC Report
                </button>
              </div>
              <div className="stx-modal-2col">
                <div className="stx-mcol">
                  {/* AllowEdit="False" in SampleConditionCheck_DetailView_Copy */}
                  {ro('Job ID', detail.jobId)}
                  {ro('Client', detail.client)}
                  {ro('Check In By', detail.checkInBy)}
                  {ro('Check In Date', detail.checkInDate ? fmtLocalDate(detail.checkInDate) : '')}
                  <div className="stx-mrow"><label>Sample Matrix</label>
                    {/* legacy CheckedListBoxEditor over the JOB's matrices, stored as
                        "; "-joined VisualMatrix Oids */}
                    <CheckedCombo
                      options={matrixOptions.map(o => o.name)}
                      values={matrixNames}
                      onChange={names => setMatrixIds(
                        names.map(n => idByName.get(n)).filter((x): x is string => !!x).join('; '))}
                      placeholder="—" summaryJoin="; " />
                  </div>
                </div>
                <div className="stx-mcol">
                  {ed('Temperature (C)', temperature, setTemperature)}
                  {ed('Corrected Temperature (C)', correctedTemperature, setCorrectedTemperature)}
                  {ed('Thermometer ID', thermometerId, setThermometerId)}
                  {ed('Sample pH', samplePh, setSamplePh)}
                  {ed('pH Paper ID', phPaperId, setPhPaperId)}
                  <div className="stx-mrow"><label>Received Date</label>
                    {/* ASPxComboBoxEditor over the job's received-date strings */}
                    <AiCombo value={recvDate}
                      options={Array.from(new Set([...(recvDate ? [recvDate] : []), ...(detail.receivedDateOptions ?? [])]))
                        .map(d => ({ value: d, label: d }))}
                      onChange={v => setRecvDate(v ?? '')} placeholder="—" /></div>
                </div>
              </div>

              <div className="stx-rd-title" style={{ marginTop: 14 }}>Check Points</div>
              <div className="stx-scc-gridwrap">
              <AppDataGrid<SccPoint>
                rows={points}
                getRowId={r => r.checkPointId}
                selectable selectedIds={cpSel} onSelectionChange={setCpSel}
                emptyText="No check points recorded"
                columns={[
                  { field: 'checkPoint', header: 'CheckPoint', width: 420 },
                  { field: 'yes', header: 'Yes', width: 60, align: 'center', sortable: false, filterable: false,
                    text: r => (r.yes ? 'Yes' : ''),
                    render: r => <input type="checkbox" checked={r.yes} onChange={() => answer(r.checkPointId, 'yes')} /> },
                  { field: 'no', header: 'No', width: 60, align: 'center', sortable: false, filterable: false,
                    text: r => (r.no ? 'No' : ''),
                    render: r => <input type="checkbox" checked={r.no} onChange={() => answer(r.checkPointId, 'no')} /> },
                  { field: 'na', header: 'N/A', width: 60, align: 'center', sortable: false, filterable: false,
                    text: r => (r.na ? 'N/A' : ''),
                    render: r => <input type="checkbox" checked={r.na} onChange={() => answer(r.checkPointId, 'na')} /> },
                  { field: 'description', header: 'Description', width: 260, sortable: false, filterable: false,
                    render: r => (
                      <input className="stx-edit stx-cellinput" value={r.description ?? ''}
                             onChange={e => describe(r.checkPointId, e.target.value)} />
                    ) },
                ]}
              />
              {/* Check Points batch-edit buttons at the GRID BOTTOM (legacy DevExpress
                  "Save changes / Cancel changes"), separate from the popup OK/Cancel. */}
              <div className="stx-grid-batchbar">
                <button className="stx-tbtn" disabled={!cpDirty} onClick={commitPoints}>
                  <i className="ti ti-device-floppy" /> Save changes</button>
                <button className="stx-tbtn" disabled={!cpDirty} onClick={revertPoints}>
                  <i className="ti ti-arrow-back-up" /> Cancel changes</button>
              </div>
              </div>

              <div className="stx-rd-title" style={{ marginTop: 14 }}>Comment</div>
              {/* New/Edit/Delete on the LEFT of the grid's header strip, with Export +
                  Maximize on the RIGHT of the SAME row (user request 2026-09-30) — via
                  the shared grid's headerLeft slot, not a separate toolbar above it. */}
              <AppDataGrid<SccComment>
                rows={comments}
                getRowId={r => r.oid}
                selectable selectedIds={cmtSel} onSelectionChange={setCmtSel}
                emptyText="No data to display"
                maximizable
                exportFilename="SampleConditionCheck_Comments"
                headerLeft={
                  <div className="stx-cbar-inline">
                    <button className="stx-cbtn" onClick={addComment}><i className="ti ti-file-plus" /> New</button>
                    <button className="stx-cbtn" disabled={cmtSel.size !== 1}
                            onClick={() => { const r = comments.find(c => c.oid === [...cmtSel][0]); if (r) void editComment(r); }}>
                      <i className="ti ti-edit" /> Edit</button>
                    <button className="stx-cbtn" disabled={cmtSel.size === 0} onClick={() => void removeComments()}>
                      <i className="ti ti-x" /> Delete</button>
                  </div>
                }
                columns={[
                  { field: 'note', header: 'Comment', width: 420 },
                  { field: 'user', header: 'User', width: 180, render: r => r.user || '—' },
                  // DATE only — legacy SampleConditionCheckComment grid shows the date,
                  // no time (owner report 2026-10-22).
                  { field: 'dateTime', header: 'DateTime', width: 150,
                    text: r => (r.dateTime ? fmtLocalDate(r.dateTime) : ''),
                    render: r => (r.dateTime ? fmtLocalDate(r.dateTime) : '—') },
                ]}
              />
            </>
          )}
        </div>
        <div className="stx-modal-foot">
          {/* Popup footer = OK / Cancel at the very end (legacy DialogController).
              "Save changes / Cancel changes" live at the Check Points grid bottom. */}
          <button className="stx-tbtn stx-primary" disabled={loading} onClick={() => void save()}>OK</button>
          <button className="stx-tbtn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AttachmentDialog({ jobOid, value, receivedOptions, onClose, onSaved }: {
  jobOid: string; value: Attachment | null;
  receivedOptions: string[];
  onClose: () => void; onSaved: () => void | Promise<void>;
}) {
  const notify = useNotify();
  const currentUser = useAuthStore(s => s.user?.name ?? '');
  const [name, setName] = useState(value?.name ?? '');
  // Category defaults to COC (enum 0). ALPACA never sets a default, so the
  // XPO enum keeps its own 0 = COC — we were defaulting to 5 = Other.
  const [category, setCategory] = useState(value?.category ?? 0);
  // Received Date DEFAULTS to the first received-date entry when empty — legacy
  // ASPxComboboxPropertyEditor_new.cs:152-158: on Attachment_DetailView, when the
  // attachment's ReceivedDate is blank (every NEW one), it sets
  // attachment.ReceivedDate = dates.lstdates.First(). On this form that first entry
  // is the transfer's own received date, which is also what the tab filters on, so
  // the new attachment lands in the list it was created from (user report 2026-10-20:
  // "showed default in ALPACA but not in Intelligence"). An EXISTING row keeps its
  // stored value; an existing row with a blank date also falls back, matching legacy.
  const [receivedDate, setReceivedDate] = useState(value?.receivedDate || receivedOptions[0] || '');
  const [comment, setComment] = useState(value?.comment ?? '');
  const [fileOid, setFileOid] = useState<string | null>(value?.fileOid ?? null);
  const [fileName, setFileName] = useState(value?.fileName ?? '');
  const fileRef = useRef<HTMLInputElement>(null);
  // Bespoke popup keyboard: Esc closes (standing rule 14).
  const modalKeys = useModalKeys(true, { onClose });

  const upload = useCallback(async (f: File) => {
    const fd = new FormData(); fd.append('file', f);
    try {
      const res = await runLoading(api.post(`${API_BASE}/${jobOid}/attachments/file`, fd),
        { blocking: true, label: 'Uploading…' });
      const d = (res.data as { data: { fileOid: string; fileName: string } }).data;
      setFileOid(d.fileOid); setFileName(d.fileName);
      // Default the Name to the file name SANS extension ("260803012.pdf" →
      // "260803012"), matching ALPACA (the LIMS popup shows the stem, not the
      // ".pdf") and the Sample Registration attachment tab, which already strips
      // it (RegistrationTabs.tsx). The user can still edit it before saving.
      if (!name) setName(d.fileName.replace(/\.[^./\\]+$/, ''));
    } catch { notify.error('Failed to upload the file.'); }
  }, [jobOid, name, notify]);

  // Download the currently-attached file (the filename renders as a link, like
  // ALPACA's FileDataPropertyEditor).
  const downloadFile = useCallback(async () => {
    if (!fileOid) return;
    try {
      const res = await runLoading(
        api.get(`${API_BASE}/attachments/file/${fileOid}/download`, { responseType: 'blob' }),
        { blocking: true, label: 'Downloading…' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName || 'attachment'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { notify.error('Failed to download the file.'); }
  }, [fileOid, fileName, notify]);

  // Eraser = clear the attachment (mirrors ALPACA's clear icon). Reset the hidden
  // input too so re-selecting the same file still fires onChange.
  const clearFile = useCallback(() => {
    setFileOid(null); setFileName('');
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const save = useCallback(async () => {
    // Attachment.Attachments carries [RuleRequiredField] in ALPACA, so a row
    // can never be saved without a file — we only guarded Name.
    if (!fileOid) { notify.warning('Attachment is required.'); return; }
    if (!name.trim()) { notify.warning('Name is required.'); return; }
    try {
      await runLoading(api.post(`${API_BASE}/${jobOid}/attachments`, {
        oid: value?.oid ?? null, name: name.trim(), category, fileOid, receivedDate, comment,
      }), { blocking: true, label: 'Saving…' });
      notify.success('Saved successfully.');
      await onSaved();
    } catch { notify.error('Failed to save the attachment.'); }
  }, [name, category, fileOid, receivedDate, comment, value, jobOid, notify, onSaved]);

  // The stored value always stays offerable, so editing a row filed under
  // another transfer never silently blanks its Received Date.
  const rdOptions = useMemo(
    () => Array.from(new Set([...(value?.receivedDate ? [value.receivedDate] : []), ...receivedOptions])),
    [value, receivedOptions]);
  const today = useMemo(() => fmtLocalDate(nowClientWire()), []);
  // Show the link + pencil + eraser only for an EXISTING saved file (Edit). A New
  // record — or an Edit whose file was just cleared — shows the box + Browse…,
  // exactly like ALPACA's upload editor.
  const showFileLink = !!fileOid && !!value?.fileOid;

  return (
    <div className="stx-modal-backdrop" onClick={onClose}>
      <div className="stx-modal stx-modal-att" onClick={e => e.stopPropagation()} {...modalKeys}>
        <StxModalHead title={value ? 'Edit Attachment' : 'New Attachment'} onClose={onClose} />
        <div className="stx-modal-body">
          {/* ALPACA's FileDataPropertyEditor has two looks, matched here:
              • an EXISTING saved file (Edit) → the filename as a download LINK with
                a PENCIL (change) + ERASER (clear) beside it;
              • no saved file yet (New, or after Clear) → a read-only path BOX + a
                "Browse..." button (owner request 2026-09-29). */}
          <div className="stx-mrow"><label>Attachment<span className="stx-req">*</span></label>
            <div className="stx-file">
              {showFileLink ? (
                <>
                  <button type="button" className="stx-link stx-linkbtn" onClick={() => void downloadFile()}>{fileName || 'Download'}</button>
                  <button type="button" className="stx-editpen" title="Change file"
                          onClick={() => fileRef.current?.click()}><i className="ti ti-pencil" /></button>
                  <button type="button" className="stx-editpen" title="Clear"
                          onClick={clearFile}><i className="ti ti-eraser" /></button>
                </>
              ) : (
                <>
                  <input type="text" className="stx-file-box" readOnly value={fileName}
                         onClick={() => fileRef.current?.click()} />
                  <button type="button" className="stx-tbtn stx-browse"
                          onClick={() => fileRef.current?.click()}>Browse...</button>
                </>
              )}
              <input ref={fileRef} type="file" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f); }} />
            </div>
          </div>
          <div className="stx-mrow"><label>Name<span className="stx-req">*</span></label>
            <input value={name} onChange={e => setName(e.target.value)} /></div>
          <div className="stx-mrow"><label>Category<span className="stx-req">*</span></label>
            <select value={category} onChange={e => setCategory(Number(e.target.value))}>
              {CATEGORIES.map((c, i) => <option key={i} value={i}>{c}</option>)}
            </select></div>
          {/* ALPACA renders Received Date as an ASPxComboBoxEditor over the
              job's received-date strings, NOT a free-text box
              (Model.xafml Attachment_DetailView + ASPxComboboxPropertyEditor_new).
              No eraser: the property is a string on a combo editor, not an FK. */}
          <div className="stx-mrow"><label>Received Date</label>
            <AiCombo value={receivedDate} options={rdOptions.map(d => ({ value: d, label: d }))}
              onChange={v => setReceivedDate(v ?? '')} placeholder="—" /></div>
          {/* Date and Operator are read-only (AllowEdit="False"), and ALPACA's
              AfterConstruction stamps BOTH on a NEW attachment (Date = now,
              Operator = the current user) — so they show on New as well, which
              is what the legacy popup displays (owner report 2026-09-28). */}
          <div className="stx-mrow"><label>Date</label>
            <div className="stx-ro-mini">{value?.date ? fmtLocalDate(value.date) : today}</div></div>
          <div className="stx-mrow"><label>Comment</label>
            <input value={comment} onChange={e => setComment(e.target.value)} /></div>
          <div className="stx-mrow"><label>Operator</label>
            <div className="stx-ro-mini">{value?.operator || currentUser || '—'}</div></div>
        </div>
        <div className="stx-modal-foot">
          <button className="stx-tbtn stx-primary" onClick={save}>OK</button>
          <button className="stx-tbtn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Note New/Edit dialog (legacy ALPACA "Note" popup) ───────────────────────
/** Strip HTML tags → plain text (grid preview + PlainText column). */
function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function NoteDialog({ jobOid, value, onClose, onSaved }: {
  jobOid: string; value: NoteRow | null; onClose: () => void; onSaved: () => void | Promise<void>;
}) {
  const notify = useNotify();
  const currentUser = useAuthStore(s => s.user?.name ?? '');
  const [title, setTitle] = useState(value?.title ?? '');
  const [sort, setSort] = useState(String(value?.sort ?? 0));
  const [fileOid, setFileOid] = useState<string | null>(value?.attachmentOid ?? null);
  const [fileName, setFileName] = useState(value?.attachmentName ?? '');
  const fileRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Remember the caret/selection in the rich-text body so a toolbar <select>
  // (font family / size) can restore it before applying — clicking a native
  // select otherwise drops the contentEditable selection.
  const rangeRef = useRef<Range | null>(null);
  const saveRange = useCallback(() => {
    const s = window.getSelection();
    if (s && s.rangeCount && bodyRef.current?.contains(s.anchorNode)) rangeRef.current = s.getRangeAt(0).cloneRange();
  }, []);
  const applyCmd = useCallback((cmd: string, val?: string) => {
    bodyRef.current?.focus();
    const s = window.getSelection();
    if (rangeRef.current && s) { s.removeAllRanges(); s.addRange(rangeRef.current); }
    document.execCommand(cmd, false, val);
    saveRange();
  }, [saveRange]);
  const today = useMemo(() => fmtLocalDateTime(nowClientWire()), []);
  // Bespoke popup keyboard: Esc closes (standing rule 14).
  const modalKeys = useModalKeys(true, { onClose });

  // Same generic FileData upload the Attachment tab uses (returns a FileData Oid).
  const upload = useCallback(async (f: File) => {
    const fd = new FormData(); fd.append('file', f);
    try {
      const res = await runLoading(api.post(`${API_BASE}/${jobOid}/attachments/file`, fd),
        { blocking: true, label: 'Uploading…' });
      const d = (res.data as { data: { fileOid: string; fileName: string } }).data;
      setFileOid(d.fileOid); setFileName(d.fileName);
    } catch { notify.error('Failed to upload the file.'); }
  }, [jobOid, notify]);

  const clearFile = useCallback(() => {
    setFileOid(null); setFileName('');
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const downloadFile = useCallback(async () => {
    if (!fileOid) return;
    try {
      const res = await runLoading(
        api.get(`${API_BASE}/attachments/file/${fileOid}/download`, { responseType: 'blob' }),
        { blocking: true, label: 'Downloading…' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName || 'attachment'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { notify.error('Failed to download the file.'); }
  }, [fileOid, fileName, notify]);

  const save = useCallback(async () => {
    const html = bodyRef.current?.innerHTML ?? value?.html ?? '';
    const plain = stripHtml(html);
    if (!title.trim()) { notify.warning('Enter the Title.'); return; }
    if (!plain) { notify.warning('Enter the Note text.'); return; }
    const body = { title: title.trim(), html, plain, sort: Number(sort) || 0, fileOid };
    try {
      if (value) {
        await runLoading(api.put(`${API_BASE}/${jobOid}/notes-tab/${value.oid}`, body),
          { blocking: true, label: 'Saving…' });
      } else {
        await runLoading(api.post(`${API_BASE}/${jobOid}/notes-tab`, body),
          { blocking: true, label: 'Saving…' });
      }
      notify.success('Saved successfully.');
      await onSaved();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      notify.error(msg || 'Failed to save the note.');
    }
  }, [title, sort, fileOid, value, jobOid, notify, onSaved]);

  return (
    <div className="stx-modal-backdrop" onClick={onClose}>
      <div className="stx-modal stx-modal-wide stx-modal-note" onClick={e => e.stopPropagation()} {...modalKeys}>
        <StxModalHead title="Note" onClose={onClose} />
        <div className="stx-modal-body">
          {/* Legacy Note popup header block: Title / Date / Attachment / sort / Author. */}
          <div className="stx-modal-2col">
            <div className="stx-mcol">
              <div className="stx-mrow"><label>Title<span className="stx-req">*</span></label>
                <input value={title} className={title.trim() ? undefined : 'sr-req-empty'}
                       onChange={e => setTitle(e.target.value)} /></div>
              <div className="stx-mrow"><label>Attachment</label>
                <div className="stx-file">
                  {fileOid ? (
                    <>
                      <button type="button" className="stx-link stx-linkbtn" onClick={() => void downloadFile()}>{fileName || 'Download'}</button>
                      <button type="button" className="stx-editpen" title="Change file" onClick={() => fileRef.current?.click()}><i className="ti ti-pencil" /></button>
                      <button type="button" className="stx-editpen" title="Clear" onClick={clearFile}><i className="ti ti-eraser" /></button>
                    </>
                  ) : (
                    <>
                      <input type="text" className="stx-file-box" readOnly value={fileName} onClick={() => fileRef.current?.click()} />
                      <button type="button" className="stx-tbtn stx-browse" onClick={() => fileRef.current?.click()}>Browse...</button>
                    </>
                  )}
                  <input ref={fileRef} type="file" style={{ display: 'none' }}
                    onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f); }} />
                </div>
              </div>
              <div className="stx-mrow"><label>Author</label>
                <div className="stx-ro-mini">{value?.author || currentUser || '—'}</div></div>
            </div>
            <div className="stx-mcol">
              <div className="stx-mrow"><label>Date</label>
                <div className="stx-ro-mini">{value?.date ? fmtLocalDateTime(value.date) : today}</div></div>
              <div className="stx-mrow"><label>sort</label>
                <input type="number" value={sort} onChange={e => setSort(e.target.value)} /></div>
            </div>
          </div>

          {/* Rich-text Note body (legacy DevExpress RichEdit → contentEditable + a
              minimal toolbar; stored as HTML, previewed as plain text). */}
          <div className="stx-rte">
            <div className="stx-rte-tab">Note</div>
            <div className="stx-rte-bar">
              <button type="button" className="stx-rte-btn" title="Undo" onMouseDown={e => { e.preventDefault(); document.execCommand('undo'); }}><i className="ti ti-arrow-back-up" /></button>
              <button type="button" className="stx-rte-btn" title="Redo" onMouseDown={e => { e.preventDefault(); document.execCommand('redo'); }}><i className="ti ti-arrow-forward-up" /></button>
              <span className="stx-rte-sep" />
              {/* Font family + size (legacy DevExpress RichEdit "Calibri" dropdown). */}
              <select className="stx-rte-select" title="Font" defaultValue="Calibri"
                      onMouseDown={saveRange} onChange={e => applyCmd('fontName', e.target.value)}>
                {['Calibri', 'Arial', 'Times New Roman', 'Verdana', 'Georgia', 'Courier New', 'Tahoma'].map(f =>
                  <option key={f} value={f}>{f}</option>)}
              </select>
              <select className="stx-rte-select stx-rte-size" title="Font size" defaultValue="3"
                      onMouseDown={saveRange} onChange={e => applyCmd('fontSize', e.target.value)}>
                <option value="1">8</option><option value="2">10</option><option value="3">12</option>
                <option value="4">14</option><option value="5">18</option><option value="6">24</option>
                <option value="7">36</option>
              </select>
              <span className="stx-rte-sep" />
              <button type="button" className="stx-rte-btn" title="Bold" onMouseDown={e => { e.preventDefault(); document.execCommand('bold'); }}><i className="ti ti-bold" /></button>
              <button type="button" className="stx-rte-btn" title="Italic" onMouseDown={e => { e.preventDefault(); document.execCommand('italic'); }}><i className="ti ti-italic" /></button>
              <button type="button" className="stx-rte-btn" title="Underline" onMouseDown={e => { e.preventDefault(); document.execCommand('underline'); }}><i className="ti ti-underline" /></button>
              <span className="stx-rte-sep" />
              <button type="button" className="stx-rte-btn" title="Bulleted list" onMouseDown={e => { e.preventDefault(); document.execCommand('insertUnorderedList'); }}><i className="ti ti-list" /></button>
              <button type="button" className="stx-rte-btn" title="Numbered list" onMouseDown={e => { e.preventDefault(); document.execCommand('insertOrderedList'); }}><i className="ti ti-list-numbers" /></button>
            </div>
            <div ref={bodyRef} className="stx-rte-body" contentEditable suppressContentEditableWarning
              onMouseUp={saveRange} onKeyUp={saveRange}
              dangerouslySetInnerHTML={{ __html: value?.html ?? '' }} />
          </div>
        </div>
        <div className="stx-modal-foot">
          <button className="stx-tbtn stx-primary" onClick={save}>OK</button>
          <button className="stx-tbtn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Received-date Edit dialog (legacy 2-column popup) ───────────────────────
const lc = (s: string | null | undefined) => (s ?? '').toLowerCase();

function ReceivedDateDialog({ jobOid, value, broughtBy, relinquishers, onClose, onSaved }: {
  jobOid: string; value: ReceivedDetail; broughtBy: { id: string; name: string }[];
  relinquishers?: Set<string>;
  onClose: () => void; onSaved: () => void | Promise<void>;
}) {
  const notify = useNotify();
  const [lk, setLk] = useState<Lookups>({ employees: [], turnarounds: [], matrices: [], categories: [] });
  const [receivedDate, setReceivedDate] = useState(toLocalInput(value.receivedDate));
  const [receivedById, setReceivedById] = useState(lc(value.receivedById));
  const [relinquishedById, setRelinquishedById] = useState(lc(value.relinquishedById));
  const [tatId, setTatId] = useState(lc(value.tatId));
  const [noOfSamples, setNoOfSamples] = useState(String(value.noOfSamples ?? 0));
  const [coolerId, setCoolerId] = useState(value.coolerId ?? '');
  const [sampleMatrixId, setSampleMatrixId] = useState(lc(value.sampleMatrixId));
  // Sample Category is a CHECKED-LIST (multi-select) in ALPACA, stored as "; "-joined
  // Oids. Track the selection as category NAMES for the shared CheckedCombo; map
  // name<->Oid via the loaded lookups (below).
  const [catNames, setCatNames] = useState<string[]>([]);
  // Bespoke popup keyboard: Esc closes (standing rule 14).
  const modalKeys = useModalKeys(true, { onClose });

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get(`${API_BASE}/received-date-lookups`);
        setLk((res.data as { data: Lookups }).data ?? { employees: [], turnarounds: [], matrices: [], categories: [] });
      } catch { /* lookups optional */ }
    })();
  }, []);

  // Sample Category name<->Oid maps + initial selection (the stored "; "-joined Oids
  // resolved to names once the category lookups arrive).
  const catByOid = useMemo(
    () => new Map(lk.categories.map(c => [c.id.toLowerCase(), c.name])), [lk.categories]);
  const catByName = useMemo(
    () => new Map(lk.categories.map(c => [c.name, c.id])), [lk.categories]);
  useEffect(() => {
    const oids = (value.sampleCategoryId ?? '').split(';').map(s => s.trim()).filter(Boolean);
    setCatNames(oids.map(o => catByOid.get(o.toLowerCase())).filter((n): n is string => !!n));
  }, [catByOid, value.sampleCategoryId]);

  // Relinquished By is scoped to the job's OWN relinquishers, not the full BroughtBy
  // master — legacy SC_ReceivedDates.RelinquishedByDataSource returns only the
  // BroughtBy rows whose Oid is in SampleCheckin.RelinquishedBy (the header set).
  // Fall back to the full list when the header is still empty (never an empty
  // dropdown) and always keep the row's CURRENT value so it can't vanish. Same rule
  // the samples-grid RelinquishedBy cell uses (user report 2026-09-30).
  const relinqOpts = useMemo(() => {
    const picked = relinquishers && relinquishers.size
      ? broughtBy.filter(b => relinquishers.has(b.id.toLowerCase()))
      : broughtBy;
    const cur = lc(value.relinquishedById);
    if (cur && !picked.some(b => b.id.toLowerCase() === cur)) {
      const self = broughtBy.find(b => b.id.toLowerCase() === cur);
      if (self) return [self, ...picked];
    }
    return picked;
  }, [broughtBy, relinquishers, value.relinquishedById]);

  const save = useCallback(async () => {
    try {
      await runLoading(api.put(`${API_BASE}/${jobOid}/received-dates/${value.oid}`, {
        receivedDate: receivedDateWire(value.receivedDate, receivedDate),
        receivedById: receivedById || null,
        relinquishedById: relinquishedById || null,
        tatId: tatId || null,
        noOfSamples: Number(noOfSamples) || 0,
        coolerId,
        sampleMatrixId: sampleMatrixId || null,
        // "; "-joined Oids of the checked categories (ALPACA CheckedListBoxEditor).
        sampleCategoryId: catNames.map(n => catByName.get(n)).filter((id): id is string => !!id).join('; ') || null,
      }), { blocking: true, label: 'Saving…' });
      notify.success('Saved successfully.');
      await onSaved();
    } catch { notify.error('Failed to save the received date.'); }
  }, [receivedDate, receivedById, relinquishedById, tatId, noOfSamples, coolerId, sampleMatrixId, catNames, catByName, jobOid, value, notify, onSaved]);

  // Legacy XAF LookupPropertyEditor look: the shared AiCombo (combo dropdown + a
  // top clear/"N/A" row = the erase). Case-insensitive id match handled inside.
  const toOpts = (list: { id: string; name: string }[]) => list.map(o => ({ value: o.id, label: o.name }));

  return (
    <div className="stx-modal-backdrop" onClick={onClose}>
      <div className="stx-modal stx-modal-wide" onClick={e => e.stopPropagation()} {...modalKeys}>
        {/* Title = the record's identity (legacy "…ReceivedDate"), 24h per §14b.6. */}
        <StxModalHead onClose={onClose}
          title={value.receivedDate ? `${fmtLocalDateTime(value.receivedDate)} - ReceivedDate` : 'Edit Received Date'} />
        <div className="stx-modal-body stx-modal-2col stx-modal-rd">
          {/* LEFT column (legacy order) */}
          <div className="stx-mcol">
            <div className="stx-mrow"><label>Received Date</label>
              {/* Minute precision — no step="1", so the time shows HH:mm not HH:mm:SS
                  (legacy parity, user request 2026-10-20). The stored seconds survive
                  via receivedDateWire (see toLocalInput). */}
              <input type="datetime-local" value={receivedDate} onChange={e => setReceivedDate(e.target.value)} /></div>
            <div className="stx-mrow"><label>Received By</label>
              <AiCombo value={receivedById} options={toOpts(lk.employees)} onChange={v => setReceivedById(v ?? '')} placeholder="—" erasable /></div>
            <div className="stx-mrow"><label>No. of Samples</label>
              <input type="number" value={noOfSamples} onChange={e => setNoOfSamples(e.target.value)} /></div>
            <div className="stx-mrow"><label>Sample Matrix</label>
              <AiCombo value={sampleMatrixId} options={toOpts(lk.matrices)} onChange={v => setSampleMatrixId(v ?? '')} placeholder="—" /></div>
            <div className="stx-mrow"><label>Sample Category</label>
              {/* Multi-select checked list (ALPACA CheckedListBoxEditor) - pick more
                  than one; the trigger shows them "; "-joined (owner report 2026-09-30). */}
              <CheckedCombo options={lk.categories.map(c => c.name)} values={catNames}
                onChange={setCatNames} placeholder="" unit="categories" summaryJoin="; " /></div>
          </div>
          {/* RIGHT column (legacy order) */}
          <div className="stx-mcol">
            <div className="stx-mrow"><label>Relinquished By</label>
              {/* Clear/null option shows "N/A" (legacy ReceivedDate popup parity — the
                  nullable BroughtBy lookup renders its none option as N/A, not "—"). */}
              <AiCombo value={relinquishedById} options={toOpts(relinqOpts)} onChange={v => setRelinquishedById(v ?? '')} placeholder="N/A" naLabel="N/A" erasable /></div>
            <div className="stx-mrow"><label>TAT</label>
              <AiCombo value={tatId} options={toOpts(lk.turnarounds)} onChange={v => setTatId(v ?? '')} placeholder="N/A" naLabel="N/A" erasable /></div>
            <div className="stx-mrow"><label>Cooler Id</label>
              <input value={coolerId} onChange={e => setCoolerId(e.target.value)} /></div>
            <div className="stx-mrow"><label>Created By</label>
              <div className="stx-ro-mini">{value.createdBy || '—'}</div></div>
            <div className="stx-mrow"><label>Created Date</label>
              <div className="stx-ro-mini">{value.createdDate ? fmtLocalDate(value.createdDate) : '—'}</div></div>
          </div>
        </div>
        <div className="stx-modal-foot">
          <button className="stx-tbtn stx-primary" onClick={save}>OK</button>
          <button className="stx-tbtn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
